<template>
  <div>
    <v-card class="transparent" flat v-if="hasClienteId">
      <v-card-title class="pl-0 pr-0">
        <div class="headline">Direcciones</div>
        <v-spacer></v-spacer>

        <cliente-direccion-create
          :cliente_id="getClienteId()"
          @refresh-direcciones="fetchDirecciones"
        />

        <v-dialog v-model="dialogUpdate" width="600px" v-if="direccionesSelected.length === 1">
          <cliente-direccion-update
            :cliente_id="getClienteId()"
            :direccion="direccionesSelected[0]"
            @refresh-direcciones="fetchDirecciones"
            @updated-direccion="setDireccion"
            @close-dialog="dialogUpdate = false"
          />
        </v-dialog>
      </v-card-title>
    </v-card>

    <v-progress-linear v-if="loading" indeterminate />

    <v-list v-if="!loading && direcciones.length">
      <v-list-item
        v-for="direccion in direcciones"
        :key="direccion.id"
      >
        <template v-slot:default="{ active }">
          <v-list-item-action>
            <v-checkbox
              v-model="direccion.selected"
              :input-value="active"
            />
          </v-list-item-action>

          <v-list-item-content>
            <v-list-item-title>{{ direccion.nombre }}</v-list-item-title>
            <v-list-item-subtitle>
              {{ direccion.calle }} {{ direccion.altura }}
            </v-list-item-subtitle>
          </v-list-item-content>

          <v-icon
            @click="setFavorita(direccion)"
            :color="direccion.is_favorita ? 'yellow darken-3' : 'grey lighten-1'"
          >
            mdi-star
          </v-icon>
        </template>
      </v-list-item>

      <v-divider />
    </v-list>

    <v-list v-else-if="!loading">
      <v-list-item>
        <v-list-item-content>
          <v-list-item-title>Sin direcciones</v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list>

    <v-footer padless v-if="direccionesSelected.length > 0">
      <v-card flat tile width="100%" class="grey darken-3 text-center">
        <v-card-text class="white--text">
          <v-btn icon large class="mx-4" @click="dialogDelete = true">
            <v-icon>mdi-delete</v-icon>
          </v-btn>

          <v-btn
            v-if="direccionesSelected.length === 1"
            icon large
            class="mx-4"
            @click="dialogUpdate = true"
          >
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
        </v-card-text>
      </v-card>
    </v-footer>

    <layout-dialog-delete
      v-if="dialogDelete"
      @close-dialog="dialogDelete = false"
      @processDelete="eliminarDirecciones(direccionesSelected)"
    >
      <template v-slot:title>Eliminar dirección</template>
      <template v-slot:message>
        ¿Estás seguro que deseas eliminar
        {{ direccionesSelected.length > 1
            ? "las direcciones seleccionadas"
            : "la dirección seleccionada"
        }}?
      </template>
    </layout-dialog-delete>
  </div>
</template>

<script>
import ClienteDireccionCreate from "@/components/shared/clientes/direcciones/cliente.direccion.create.vue";
import ClienteDireccionUpdate from "@/components/shared/clientes/direcciones/cliente.direccion.update.vue";
import LayoutDialogDelete from "@/layouts/LayoutDialogDelete";

import ClienteDireccionesAPI from "@/api/clientes/direcciones.js";

export default {
  name: "ClienteDirecciones",
  components: {
    ClienteDireccionCreate,
    ClienteDireccionUpdate,
    LayoutDialogDelete,
  },
  props: {
    onlySelect: Boolean,
    cliente: Object,
  },

  data() {
    return {
      dialogDelete: false,
      dialogUpdate: false,
      direcciones: [],
      loading: false,
      noImage: require("@/assets/man-woman.jpg"),
    };
  },

  computed: {
    hasClienteId() {
      return this.cliente || this.$route.params.id_cliente;
    },

    direccionesSelected() {
      return this.direcciones.filter((d) => d.selected);
    },
  },

  methods: {
    getClienteId() {
      return this.onlySelect
        ? this.cliente.id
        : this.$route.params.id_cliente;
    },

    fetchDirecciones() {
      const id = this.getClienteId();
      if (!id) return;

      this.loading = true;
      ClienteDireccionesAPI.index(id)
        .then((r) => {
          this.direcciones = r.data.map(d => ({ ...d, selected: false }));
        })
        .catch((e) => {
          this.$updateBus.$emit("showSnackBar", {
            show: true,
            button: false,
            color: "error",
            text: "Error al cargar direcciones",
            icon: "mdi-alert",
            timeout: 3000,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    setDireccion(nueva) {
      this.direcciones = this.direcciones.map((d) =>
        d.id === nueva.id ? nueva : d
      );
    },

    setFavorita(direccion) {
      const cliente_id = this.getClienteId();

      ClienteDireccionesAPI.set_favorita(cliente_id, direccion.id)
        .then(() => this.fetchDirecciones())
        .catch((e) => {
          this.$updateBus.$emit("showSnackBar", {
            show: true,
            button: false,
            color: "error",
            text: "Error al actualizar dirección favorita",
            icon: "mdi-alert",
            timeout: 3000,
          });
        });
    },

    eliminarDirecciones(direcciones) {
      const multiple = direcciones.length > 1;
      const idsToDelete = direcciones.map(d => d.id);
      const apiCall = multiple
        ? ClienteDireccionesAPI.deleteMultiple(idsToDelete)
        : ClienteDireccionesAPI.delete(direcciones);

      apiCall
        .then(() => {
          this.$updateBus.$emit("showSnackBar", {
            show: true,
            button: false,
            color: "success",
            text: multiple
              ? "Direcciones eliminadas"
              : "Dirección eliminada",
            icon: "mdi-check",
            timeout: 2500,
          });
        })
        .catch((e) => {
          const msg =
            e.response?.status === 409
              ? e.response.data.data
              : e.response?.data?.data || "Problema inesperado";

          this.$updateBus.$emit("showSnackBar", {
            show: true,
            button: false,
            color: "error",
            text: msg,
            icon: "mdi-alert",
            timeout: 4500,
          });
        })
        .finally(() => {
          this.dialogDelete = false;
          this.fetchDirecciones();
        });
    },
  },

  created() {
    this.fetchDirecciones();
  },
};
</script>

<style lang="scss" scoped></style>
